export interface GhostFrame { t: number; x: number; y: number; a: number; }

export interface DataPoint {
  date: string;
  close: number;
}

export interface TickerInfo {
  symbol: string;
  name: string;
  exchange: string;
  volatility: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'insane';
  performance: number;
  dataPoints: DataPoint[];
  meta: {
    startDate: string;
    endDate: string;
    minPrice: number;
    maxPrice: number;
    totalPoints: number;
  };
  crashPeriod?: {
    start: string;
    end: string;
  };
  defunct?: boolean;
  trustmrr?: {
    icon: string;
    currentMrr: number;
    revenueLast30Days: number;
    slug: string;
    founder: string;
    xHandle: string;
    xPicture: string;
    website: string;
    category: string;
    country: string;
    description: string;
    weeklyDataPoints: DataPoint[];
  };
}

export interface IpoConfig {
  symbol: string;
  name: string;
  active: boolean;
}

export interface IpoTickerResponse {
  symbol: string;
  name: string;
  interval: string;
  dataPoints: DataPoint[];
  nextUpdate: number;
  marketOpen: string;
  marketClose: string;
}

type TickerMap = Record<string, TickerInfo>;

const IS_LOCAL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
export const PROXY_URL = IS_LOCAL
  ? `http://${window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'localhost' : window.location.hostname}:8787`
  : 'https://api.stonkrider.com';

let cache: TickerMap | null = null;
let featuredSymbols: Set<string> | null = null;
let hofCache: TickerMap | null = null;
let crashedCache: TickerMap | null = null;
let ipoConfigCache: IpoConfig[] | null = null;
let startupCache: Record<string, TickerInfo> | null = null;

export async function loadTickers(): Promise<TickerMap> {
  if (cache) return cache;
  const res = await fetch('/data/featured-tickers.json');
  cache = await res.json();
  featuredSymbols = new Set(Object.keys(cache!));
  return cache!;
}

export async function loadHallOfFameTickers(): Promise<TickerMap> {
  if (hofCache) return hofCache;
  const res = await fetch('/data/hall-of-fame-tickers.json');
  hofCache = await res.json();
  return hofCache!;
}

export function getHallOfFameList(tickers: TickerMap): TickerInfo[] {
  return Object.values(tickers);
}

export async function loadCrashedTickers(): Promise<TickerMap> {
  if (crashedCache) return crashedCache;
  const res = await fetch('/data/crashed-tickers.json');
  crashedCache = await res.json();
  return crashedCache!;
}

export async function loadFeaturedStartups(): Promise<Record<string, TickerInfo>> {
  if (startupCache) return startupCache;
  try {
    const res = await fetch('/data/featured-startups.json');
    if (!res.ok) { startupCache = {}; return startupCache; }
    startupCache = await res.json();
    return startupCache!;
  } catch { startupCache = {}; return startupCache; }
}

export async function loadIpoConfig(): Promise<IpoConfig[]> {
  if (ipoConfigCache) return ipoConfigCache;
  try {
    const res = await fetch('/data/ipo-tickers.json');
    if (!res.ok) { ipoConfigCache = []; return ipoConfigCache; }
    const data = await res.json();
    ipoConfigCache = Array.isArray(data) ? data : [];
  } catch {
    ipoConfigCache = [];
  }
  return ipoConfigCache;
}

export function isIpoTicker(symbol: string): boolean {
  return ipoConfigCache?.some(c => c.symbol === symbol.toUpperCase() && c.active) ?? false;
}

export async function fetchIpoTicker(symbol: string, interval = '5m'): Promise<IpoTickerResponse | null> {
  try {
    const res = await fetch(`${PROXY_URL}/ipo/${encodeURIComponent(symbol.toUpperCase())}?interval=${interval}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function ipoResponseToTickerInfo(ipo: IpoTickerResponse): TickerInfo {
  const points = ipo.dataPoints;
  const vol = points.length >= 10 ? calcVolatility(points) : 0;
  const perf = points.length >= 2
    ? Math.round(((points[points.length - 1].close / points[0].close) - 1) * 1000) / 10
    : 0;
  return {
    symbol: ipo.symbol,
    name: ipo.name,
    exchange: 'NASDAQ',
    volatility: vol,
    difficulty: calcDifficulty(points),
    performance: perf,
    dataPoints: points,
    meta: {
      startDate: points[0]?.date || '',
      endDate: points[points.length - 1]?.date || '',
      minPrice: points.length ? Math.min(...points.map(p => p.close)) : 0,
      maxPrice: points.length ? Math.max(...points.map(p => p.close)) : 0,
      totalPoints: points.length,
    },
  };
}

export function getCrashedList(tickers: TickerMap): TickerInfo[] {
  return Object.values(tickers);
}

export class ServiceUnavailableError extends Error {
  constructor() { super('Service temporarily unavailable'); }
}

export async function fetchLiveTicker(symbol: string): Promise<TickerInfo | null> {
  if (cache && cache[symbol.toUpperCase()]) return cache[symbol.toUpperCase()];

  try {
    const res = await fetch(`${PROXY_URL}/?s=${encodeURIComponent(symbol)}`);
    if (res.status === 503 || res.status === 429 || res.status >= 500) throw new ServiceUnavailableError();
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.dataPoints?.length) return null;

    const points: DataPoint[] = data.dataPoints;
    const vol = calcVolatility(points);
    const perf = Math.round(((points[points.length - 1].close / points[0].close) - 1) * 1000) / 10;

    const ticker: TickerInfo = {
      symbol: data.symbol,
      name: data.name,
      exchange: data.exchange,
      volatility: vol,
      difficulty: calcDifficulty(points),
      performance: perf,
      dataPoints: points,
      meta: {
        startDate: points[0].date,
        endDate: points[points.length - 1].date,
        minPrice: Math.min(...points.map(p => p.close)),
        maxPrice: Math.max(...points.map(p => p.close)),
        totalPoints: points.length,
      },
    };

    if (cache) cache[ticker.symbol] = ticker;
    return ticker;
  } catch (err) {
    if (err instanceof ServiceUnavailableError) throw err;
    throw new ServiceUnavailableError();
  }
}

export function parseTrustMrrUrl(input: string): string | null {
  const match = input.match(/trustmrr\.com\/startup\/([a-z0-9\-]+)/i);
  return match ? match[1].toLowerCase() : null;
}

export async function fetchTrustMrrTicker(slug: string): Promise<TickerInfo | null> {
  try {
    const res = await fetch(`${PROXY_URL}/trustmrr/${encodeURIComponent(slug)}`);
    if (res.status === 503 || res.status === 429 || res.status >= 500) throw new ServiceUnavailableError();
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.dataPoints?.length) return null;

    const points: DataPoint[] = data.dataPoints;
    const vol = calcVolatility(points);
    const perf = Math.round(((points[points.length - 1].close / points[0].close) - 1) * 1000) / 10;

    return {
      symbol: data.symbol,
      name: data.name,
      exchange: 'TrustMRR',
      volatility: vol,
      difficulty: calcDifficulty(points),
      performance: perf,
      dataPoints: points,
      meta: {
        startDate: points[0].date,
        endDate: points[points.length - 1].date,
        minPrice: Math.min(...points.map(p => p.close)),
        maxPrice: Math.max(...points.map(p => p.close)),
        totalPoints: points.length,
      },
      trustmrr: { ...data.trustmrr, weeklyDataPoints: data.weeklyDataPoints || [] },
    };
  } catch (err) {
    if (err instanceof ServiceUnavailableError) throw err;
    throw new ServiceUnavailableError();
  }
}

function calcVolatility(points: DataPoint[]): number {
  if (points.length < 10) return 0;
  const returns: number[] = [];
  for (let i = 1; i < points.length; i++) {
    returns.push(Math.log(points[i].close / points[i - 1].close));
  }
  const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
  const variance = returns.reduce((a, r) => a + (r - mean) ** 2, 0) / returns.length;
  return Math.round(Math.sqrt(variance) * Math.sqrt(252) * 1000) / 1000;
}

export function getTickerList(tickers: TickerMap): TickerInfo[] {
  if (!featuredSymbols) return Object.values(tickers);
  return Object.values(tickers).filter(t => featuredSymbols!.has(t.symbol));
}

function downsampleKeepPeaks(points: DataPoint[], target: number): DataPoint[] {
  const keep = new Set<number>([0, points.length - 1]);

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1].close;
    const curr = points[i].close;
    const next = points[i + 1].close;
    if ((curr >= prev && curr >= next) || (curr <= prev && curr <= next)) {
      keep.add(i);
    }
  }

  if (keep.size > target) {
    const scored = [...keep].map(i => {
      if (i === 0 || i === points.length - 1) return { i, score: Infinity };
      const diff = Math.abs(points[i].close - points[i - 1].close) + Math.abs(points[i].close - points[i + 1].close);
      return { i, score: diff };
    });
    scored.sort((a, b) => b.score - a.score);
    const topSet = new Set(scored.slice(0, target).map(s => s.i));
    const indices = [...topSet].sort((a, b) => a - b);
    return indices.map(i => points[i]);
  }

  const remaining = target - keep.size;
  if (remaining > 0) {
    const step = points.length / (remaining + 1);
    for (let i = 1; i <= remaining; i++) {
      keep.add(Math.round(i * step));
    }
  }

  const indices = [...keep].sort((a, b) => a - b);
  return indices.map(i => points[i]);
}

export function filterByPeriod(points: DataPoint[], period: string, ticker?: TickerInfo): DataPoint[] {
  if (!points.length) return points;

  if (period.toLowerCase() === 'crash' && ticker?.crashPeriod) {
    const crashPts = points.filter(p => p.date >= ticker.crashPeriod!.start && p.date <= ticker.crashPeriod!.end);
    if (crashPts.length > 350) return downsampleKeepPeaks(crashPts, 250);
    return crashPts;
  }

  const end = new Date(points[points.length - 1].date);
  let start: Date;

  switch (period.toLowerCase()) {
    case '1d': start = new Date(end); start.setDate(start.getDate() - 1); break;
    case '1m': start = new Date(end); start.setMonth(start.getMonth() - 1); break;
    case '3m': start = new Date(end); start.setMonth(start.getMonth() - 3); break;
    case '6m': start = new Date(end); start.setMonth(start.getMonth() - 6); break;
    case '1y': start = new Date(end); start.setFullYear(start.getFullYear() - 1); break;
    case '5y':
    case 'all': start = new Date(end); start.setFullYear(start.getFullYear() - 5); break;
    default: return points;
  }

  const startStr = start.toISOString().split('T')[0];
  return points.filter(p => p.date >= startStr);
}

export function calcPerformance(points: DataPoint[]): number {
  if (points.length < 2) return 0;
  return Math.round(((points[points.length - 1].close / points[0].close) - 1) * 1000) / 10;
}

export function calcDifficulty(points: DataPoint[], weekly?: boolean): 'easy' | 'medium' | 'hard' | 'insane' {
  if (points.length < 10) return 'easy';
  const returns: number[] = [];
  for (let i = 1; i < points.length; i++) {
    returns.push(Math.log(points[i].close / points[i - 1].close));
  }
  const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
  const variance = returns.reduce((a, r) => a + (r - mean) ** 2, 0) / returns.length;
  const vol = Math.sqrt(variance) * Math.sqrt(weekly ? 52 : 252);

  if (weekly) {
    if (vol < 2) return 'easy';
    if (vol < 4) return 'medium';
    if (vol < 6) return 'hard';
    return 'insane';
  }
  if (vol < 0.2) return 'easy';
  if (vol < 0.4) return 'medium';
  if (vol < 0.7) return 'hard';
  return 'insane';
}

export async function signChallenge(ticker: string, period: string, score: number, smooth?: boolean): Promise<string | null> {
  try {
    const payload: Record<string, unknown> = { ticker: ticker.toUpperCase(), period: period.toLowerCase(), score: Math.floor(score) };
    if (smooth) payload.smooth = true;
    const res = await fetch(`${PROXY_URL}/challenge/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.hash || null;
  } catch {
    return null;
  }
}

export async function verifyChallenge(ticker: string, period: string, score: number, hash: string, smooth?: boolean): Promise<boolean> {
  try {
    const payload: Record<string, unknown> = { ticker: ticker.toUpperCase(), period: period.toLowerCase(), score: Math.floor(score), hash };
    if (smooth) payload.smooth = true;
    const res = await fetch(`${PROXY_URL}/challenge/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.valid === true;
  } catch {
    return false;
  }
}

export interface SiteStats {
  totalDollarsTraded: number;
  totalRides: number;
  totalCrashes: number;
}

export async function fetchStats(mode: 'stocks' | 'startups' = 'stocks'): Promise<SiteStats | null> {
  try {
    const res = await fetch(`${PROXY_URL}/stats?mode=${mode}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function incrementStats(dollars: number, crashes: number, mode: 'stocks' | 'startups' = 'stocks'): Promise<void> {
  fetch(`${PROXY_URL}/stats/increment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ dollars: Math.round(dollars * 100) / 100, crashes, mode }),
  }).catch(() => {});
}

export interface DailyLeaderboardEntry {
  nickname: string;
  score: number;
  playerId: string;
  country?: string;
}

export interface DailyChallenge {
  ticker: string;
  period: string;
  date: string;
  smooth: boolean;
  sessionId: string;
  leaderboard: DailyLeaderboardEntry[];
  totalPlayers: number;
}

export interface DailyScoreResult {
  rank: number;
  leaderboard: DailyLeaderboardEntry[];
  totalPlayers: number;
  personalBest: number;
  myEntry: DailyLeaderboardEntry | null;
}

export async function fetchDailyChallenge(full = false): Promise<DailyChallenge | null> {
  try {
    const res = await fetch(`${PROXY_URL}/daily${full ? '?full=1' : ''}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export interface GameStatsForSubmit {
  flips: number;
  crashes: number;
  maxStreak: number;
  time: number;
  finished: boolean;
}

export async function submitDailyScore(sessionId: string, score: number, nickname: string, playerId: string, stats?: GameStatsForSubmit): Promise<DailyScoreResult | null> {
  try {
    const res = await fetch(`${PROXY_URL}/daily/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, score: Math.floor(score), nickname, playerId, stats }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const FUN_NAMES = [
  'DiamondHands', 'PaperTrader', 'MoonRider', 'BagHolder', 'BullRunner',
  'DipBuyer', 'ShortSqueeze', 'HODLer', 'ToTheMoon', 'StockJockey',
  'WallStreetApe', 'TendieKing', 'RocketMan', 'CrashPilot', 'ChartSurfer',
  'BearSlayer', 'GreenCandle', 'RedCandle', 'StonkMaster', 'YOLOer',
  'VolatilityKing', 'MarginCall', 'PumpChaser', 'DumpDodger', 'FlipMaster',
];

export function getPlayerId(): string {
  let id = localStorage.getItem('stonkrider_player_id');
  if (!id) {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    id = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('stonkrider_player_id', id);
  }
  return id;
}

export function getNickname(): string {
  let nick = localStorage.getItem('stonkrider_nickname');
  if (!nick) {
    const name = FUN_NAMES[Math.floor(Math.random() * FUN_NAMES.length)];
    nick = `${name}${Math.floor(Math.random() * 100)}`;
    localStorage.setItem('stonkrider_nickname', nick);
  }
  return nick;
}

export function setNickname(nick: string): void {
  localStorage.setItem('stonkrider_nickname', nick);
}

export interface TrackBest {
  score: number;
  time: number;
  date: string;
}

export function trackBestId(ticker: string, period: string, smooth?: boolean): string {
  return `${ticker}:${period}${smooth ? ':s' : ''}`;
}

export function getTrackBest(id: string): TrackBest | null {
  try {
    const raw = localStorage.getItem(`stonkrider_pb_${id}`);
    return raw ? JSON.parse(raw) as TrackBest : null;
  } catch {
    return null;
  }
}

export function setTrackBest(id: string, best: TrackBest): void {
  try { localStorage.setItem(`stonkrider_pb_${id}`, JSON.stringify(best)); } catch {  }
}

export function getDailyBest(date: string): number {
  return parseInt(localStorage.getItem(`stonkrider_daily_best_${date}`) || '0', 10);
}

export function setDailyBest(date: string, score: number): void {
  const current = getDailyBest(date);
  if (score > current) localStorage.setItem(`stonkrider_daily_best_${date}`, String(score));
}

export interface TrackLeaderboardEntry {
  nickname: string;
  score: number;
  time: number;
  playerId?: string;
  country?: string;
}

export interface TrackLeaderboard {
  score: TrackLeaderboardEntry[];
  time: TrackLeaderboardEntry[];
}

export interface LeaderboardCheckResult {
  top: TrackLeaderboard;
  sessionId: string;
}

export interface LeaderboardSubmitResult {
  score: TrackLeaderboardEntry[];
  time: TrackLeaderboardEntry[];
  submitted: boolean;
  alreadyInBoard: boolean;
}

export async function fetchTrackLeaderboard(ticker: string, period: string): Promise<TrackLeaderboard | null> {
  try {
    const res = await fetch(`${PROXY_URL}/leaderboard/${encodeURIComponent(ticker.toUpperCase())}/${period.toLowerCase()}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function checkTrackLeaderboard(ticker: string, period: string): Promise<LeaderboardCheckResult | null> {
  try {
    const res = await fetch(`${PROXY_URL}/leaderboard/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticker: ticker.toUpperCase(), period: period.toLowerCase() }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function signLeaderboard(ticker: string, period: string, score: number, time: number): Promise<string | null> {
  try {
    const res = await fetch(`${PROXY_URL}/leaderboard/sign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticker: ticker.toUpperCase(), period: period.toLowerCase(), score: Math.floor(score), time: Math.round(time * 10) / 10 }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.hash || null;
  } catch {
    return null;
  }
}

export async function submitTrackLeaderboard(
  ticker: string, period: string, score: number, time: number,
  nickname: string, playerId: string, gameStats: GameStatsForSubmit,
  sessionId: string, hash: string,
): Promise<LeaderboardSubmitResult | null> {
  try {
    const res = await fetch(`${PROXY_URL}/leaderboard/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticker: ticker.toUpperCase(), period: period.toLowerCase(),
        score: Math.floor(score), time: Math.round(time * 10) / 10,
        nickname, playerId, gameStats, sessionId, hash,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const GHOST_BYTES_PER_FRAME = 14;
const GHOST_HEADER = 4;
const GHOST_MAX_FRAMES = 80000;

function encodeGhostFrames(frames: GhostFrame[]): Uint8Array {
  const buf = new ArrayBuffer(GHOST_HEADER + frames.length * GHOST_BYTES_PER_FRAME);
  const view = new DataView(buf);
  view.setUint32(0, frames.length, true);
  for (let i = 0; i < frames.length; i++) {
    const f = frames[i];
    const off = GHOST_HEADER + i * GHOST_BYTES_PER_FRAME;
    view.setUint32(off, Math.round(f.t * 60), true);
    view.setInt32(off + 4, Math.round(f.x), true);
    view.setInt16(off + 8, Math.round(f.y), true);
    view.setInt32(off + 10, Math.round(f.a * 1000000), true);
  }
  return new Uint8Array(buf);
}

function decodeGhostFrames(data: Uint8Array): GhostFrame[] {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const count = view.getUint32(0, true);
  const expectedBytes = GHOST_HEADER + count * GHOST_BYTES_PER_FRAME;
  if (count > GHOST_MAX_FRAMES || expectedBytes > data.byteLength) return [];
  const frames: GhostFrame[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const off = GHOST_HEADER + i * GHOST_BYTES_PER_FRAME;
    frames[i] = {
      t: view.getUint32(off, true) / 60,
      x: view.getInt32(off + 4, true),
      y: view.getInt16(off + 8, true),
      a: view.getInt32(off + 10, true) / 1000000,
    };
  }
  return frames;
}

async function compressBytes(data: Uint8Array): Promise<Uint8Array> {
  if (typeof CompressionStream !== 'undefined') {
    const stream = new Blob([data.buffer as ArrayBuffer]).stream().pipeThrough(new CompressionStream('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }
  const { deflate } = await import('pako');
  return deflate(data);
}

async function decompressBytes(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream !== 'undefined') {
    const stream = new Blob([data.buffer as ArrayBuffer]).stream().pipeThrough(new DecompressionStream('deflate'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }
  const { inflate } = await import('pako');
  return inflate(data);
}

export async function uploadGhost(frames: GhostFrame[]): Promise<string | null> {
  try {
    const encoded = encodeGhostFrames(frames);
    const compressed = await compressBytes(encoded);
    const res = await fetch(`${PROXY_URL}/ghost`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: compressed.buffer as ArrayBuffer,
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.ghostId || null;
  } catch {
    return null;
  }
}

export async function fetchGhost(ghostId: string): Promise<GhostFrame[] | null> {
  try {
    const res = await fetch(`${PROXY_URL}/ghost/${ghostId}`);
    if (!res.ok) return null;
    const compressed = new Uint8Array(await res.arrayBuffer());
    const encoded = await decompressBytes(compressed);
    return decodeGhostFrames(encoded);
  } catch {
    return null;
  }
}
